/**
 * Shared logic to hydrate Redux from getJourneyData and to determine the route
 * to resume at when landing on /term-plan with a stored application number.
 */

import { journeyActions } from '../../store/journeySlice';
import { resolveJourneyApplicationNumbers } from './journeyEncryption';
import { setStoredApplicationNumber, setStoredCaseId } from './termJourneyStorage';
import { mergeCumulativeStagesOrdered } from './journeyLeadFields';
import { buildProposalProgressFromCumulative } from './proposalCumulativeHelpers';
import { pickDocumentPreviewFields } from './documentFileMeta';

function parseStages(stages) {
  if (stages == null) return null;
  if (typeof stages === 'object') return stages;
  try {
    return typeof stages === 'string' ? JSON.parse(stages) : null;
  } catch {
    return null;
  }
}

export function normalizeJourneyStages(journey) {
  const cumulative = parseStages(
    journey?.stages ?? journey?.cummulativeJourneyData ?? journey?.c_cummulativeJourneyData,
  );
  const current = parseStages(journey?.currentStageData ?? journey?.c_currentStageData);
  return {
    ...(cumulative && typeof cumulative === 'object' ? cumulative : {}),
    ...(current && typeof current === 'object' ? current : {}),
  };
}

/**
 * Stage 9.0 (e.g. ProposalSummaryWithPVCNewTab) stores `documentsUploaded` as a JSON string:
 * `{ "photograph": { "uploaded": true, "fileName": "..." }, ... }`
 * Normalize to the same Redux `journey.documents` slot shape as local uploads (__fileMeta + name).
 */
export function parseDocumentsUploadedToReduxSlots(raw) {
  if (raw == null) return {};
  let obj = raw;
  if (typeof raw === 'string') {
    const s = raw.trim();
    if (!s) return {};
    try {
      obj = JSON.parse(s);
    } catch {
      return {};
    }
  }
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return {};
  const out = {};
  for (const [slot, meta] of Object.entries(obj)) {
    if (!meta || typeof meta !== 'object') continue;
    const up = meta.uploaded;
    const uploaded =
      up === true ||
      up === 'true' ||
      String(up).toLowerCase() === 'y' ||
      String(up).toLowerCase() === 'yes';
    const previewFields = pickDocumentPreviewFields(meta);
    const hasPreview = Object.keys(previewFields).length > 0;
    if (!uploaded && !meta.__fileMeta && !hasPreview) continue;
    const fileName =
      meta.fileName ||
      meta.filename ||
      meta.file ||
      meta.name ||
      'document';
    const row = {
      __fileMeta: true,
      name: String(fileName),
      fromJourneyDocumentsUploaded: true,
    };
    Object.assign(row, previewFields);
    out[slot] = row;
  }
  return out;
}

/** Merge `documentsUploaded` from every flattened stage object (normalized cumulative + current). */
export function mergeDocumentsUploadedFromParsedStages(parsed) {
  if (!parsed || typeof parsed !== 'object') return {};
  const merged = {};
  for (const v of Object.values(parsed)) {
    if (!v || typeof v !== 'object' || Array.isArray(v)) continue;
    Object.assign(merged, parseDocumentsUploadedToReduxSlots(v.documentsUploaded));
  }
  return merged;
}

/** Deep walk (raw journey response) for `documentsUploaded` anywhere (nested / alternate shapes). */
export function collectDocumentsUploadedDeep(root) {
  const merged = {};
  const visited = new Set();
  const walk = (node) => {
    if (node == null) return;
    if (typeof node !== 'object') return;
    if (visited.has(node)) return;
    visited.add(node);
    if (Object.prototype.hasOwnProperty.call(node, 'documentsUploaded')) {
      Object.assign(merged, parseDocumentsUploadedToReduxSlots(node.documentsUploaded));
    }
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    Object.values(node).forEach(walk);
  };
  walk(root);
  return merged;
}

function parseStageNumber(value) {
  if (typeof value === 'number') return value;
  if (typeof value !== 'string') return Number.NaN;
  const match = value.match(/(\d+(?:\.\d+)?)/);
  return match ? parseFloat(match[1]) : Number.NaN;
}

const JOURNEY_STAGE_RANK_GROUPS = [
  ['Stage 1.0 Lead', 'lead_Basic', '1', '1.0', 'NishchitPension Stage 1'],
  ['Stage 1.1 Lead', '1.1', 'NishchitPension Stage 1.1'],
  ['lead_Personal'],
  ['Stage 1.2 Lead', '1.2', 'NishchitPension Stage 1.2'],
  ['Stage 1.4 Lead', '1.4', 'NishchitPension Stage 1.4'],
  ['lead_LifeAssured', 'lead_Life_Assured'],
  ['Stage 2.0 Lead', '2', '2.0', 'NishchitPension Stage 2.0'],
  ['Stage 3.0 Quote', '3', '3.0', 'NishchitPension Stage 3.0'],
  ['Stage 4.0 QuoteSummary', 'Stage 4.0 Quote Summary', '4', '4.0', 'NishchitPension Stage 4.0'],
  ['Stage 4.1 Payment', 'NishchitPension Stage 4.1'],
  ['Stage 5.0 PaymentGatewayRedirection', 'PaymentGatewayRedirection'],
  [
    'Stage 5.0 CustomerVerificationPage',
    'CustomerVerificationPage',
    'Stage 4.2 CustomerVerification',
    'NishchitPension Stage 4.2',
  ],
  ['Stage 6.0 PaymentReceiptDownloadPage'],
  ['Stage 7.0 CustomerVerificationPage', 'CustomerVerificationPageStage7'],
  ['Stage 5.0 Proposal', 'NishchitPension Stage 5.0'],
  ['PR_PersonalDetails', 'PL_PersonalDetails', 'Stage 8.0 ProposalFormScreen1', 'Stage 8.0 Proposal'],
  ['PR_JobDetails', 'PL_JobDetails'],
  ['PR_BankDetails', 'PL_BankDetails', 'Stage 8.1 ProposalFormScreen2', 'Stage 8.1 Proposal'],
  ['LA_LifeAssured', 'Stage 8.2 ProposalFormScreen3', 'Stage 8.2 Proposal'],
  ['Stage 8.3 ProposalFormScreen4', 'Stage 8.3 Proposal'],
  ['PR_Nominee', 'PL_Nominee', 'Stage 8.4 ProposalFormScreen5', 'Stage 8.4 Proposal'],
  ['ML_LifestyleDetails'],
  ['ML_MedicalDetails'],
  [
    'PR_Declaration',
    'PL_Declaration',
    'Stage 8.5 ProposalFormScreen6',
    'Stage 8.5 Proposal',
    'Stage 8.5 ProposalFormScreen7',
    'Stage 8.5 ProposalFormScreen8',
  ],
  ['Stage 9.0 ProposalSummaryWithPVCNewTab', 'NishchitPension Stage 9.0'],
  ['Stage 9.1 OTPVerificationPayRedirection'],
  ['Stage 9.2 OTPSubmissionWithPayNow'],
  ['Stage 10.0 ThankYouPageReverseFeed', 'NishchitPension Stage 10.0 Thank You'],
];

const JOURNEY_STAGE_RANK_BY_NORMALIZED = JOURNEY_STAGE_RANK_GROUPS.reduce((ranks, group, index) => {
  group.forEach((label) => {
    ranks[normalizeStageKey(label)] = (index + 1) * 10;
  });
  return ranks;
}, {});

function normalizeStageKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '');
}

function isIgnoredResumeStageKey(key) {
  return normalizeStageKey(key) === 'journey_generic';
}

function rankJourneyStage(stage) {
  const normalized = normalizeStageKey(stage);
  if (!normalized) return 0;
  if (JOURNEY_STAGE_RANK_BY_NORMALIZED[normalized]) {
    return JOURNEY_STAGE_RANK_BY_NORMALIZED[normalized];
  }
  const n = parseStageNumber(stage);
  if (Number.isNaN(n)) return 0;
  if (n >= 10) return 400;
  if (n >= 9) return 350 + Math.round((n - 9) * 10);
  if (n >= 8) return 200 + Math.round((n - 8) * 10);
  if (n >= 7) return 180;
  if (n >= 6) return 160;
  if (n >= 5) return 120;
  if (n >= 4) return 80;
  if (n >= 3) return 70;
  if (n >= 2) return 50;
  return 10 + Math.round(n * 10);
}

export function getFurthestStageFromJourney(journey) {
  const parsed = normalizeJourneyStages(journey);
  const declared = String(
    journey?.currentStage ?? journey?.current_stage ?? journey?.c_currentStage ?? '',
  ).trim();
  return getFurthestStageToken(parsed, declared);
}

function exactJourneyStageRank(stage) {
  return JOURNEY_STAGE_RANK_BY_NORMALIZED[normalizeStageKey(stage)] || 0;
}

function isBetterExactResumeStage(key, keyRank, bestKey, bestRank, preferredStage) {
  if (keyRank > bestRank) return true;
  if (keyRank < bestRank || !bestKey) return !bestKey && keyRank > 0;
  if (normalizeStageKey(key) === normalizeStageKey(preferredStage)) return true;
  if (normalizeStageKey(bestKey) === normalizeStageKey(preferredStage)) return false;
  const group = JOURNEY_STAGE_RANK_GROUPS[keyRank / 10 - 1];
  if (!Array.isArray(group)) return false;
  const keyIdx = group.findIndex((label) => normalizeStageKey(label) === normalizeStageKey(key));
  const bestIdx = group.findIndex((label) => normalizeStageKey(label) === normalizeStageKey(bestKey));
  return keyIdx !== -1 && (bestIdx === -1 || keyIdx < bestIdx);
}

function getFurthestStageToken(parsed, preferredStage) {
  if (!parsed || typeof parsed !== 'object') return '';
  let bestExact = '';
  let bestExactRank = 0;
  let bestFallback = '';
  let bestFallbackRank = 0;
  Object.keys(parsed).forEach((key) => {
    if (isIgnoredResumeStageKey(key)) return;
    const exact = exactJourneyStageRank(key);
    if (exact > 0) {
      if (isBetterExactResumeStage(key, exact, bestExact, bestExactRank, preferredStage)) {
        bestExactRank = exact;
        bestExact = key;
      }
    } else {
      const fallback = rankJourneyStage(key);
      if (fallback > bestFallbackRank) {
        bestFallbackRank = fallback;
        bestFallback = key;
      }
    }
  });
  return bestExact || bestFallback;
}

function getMaxStageTokenFromJourney(journey, parsed) {
  const candidates = [];
  const directCurrent = journey?.currentStage ?? journey?.current_stage ?? journey?.c_currentStage;
  if (directCurrent) candidates.push(String(directCurrent));

  const currentStageData = parseStages(journey?.currentStageData ?? journey?.c_currentStageData);
  if (currentStageData && typeof currentStageData === 'object') {
    Object.keys(currentStageData).forEach((k) => candidates.push(String(k)));
    Object.values(currentStageData).forEach((v) => {
      if (v && typeof v === 'object' && v.currentStage) candidates.push(String(v.currentStage));
    });
  }

  Object.keys(parsed || {}).forEach((k) => candidates.push(String(k)));
  Object.values(parsed || {}).forEach((v) => {
    if (v && typeof v === 'object' && v.currentStage) candidates.push(String(v.currentStage));
  });

  let best = '';
  let bestNum = Number.NEGATIVE_INFINITY;
  candidates.forEach((token) => {
    const n = parseStageNumber(token);
    if (!Number.isNaN(n) && n > bestNum) {
      bestNum = n;
      best = token;
    }
  });
  return best || '';
}

function getStageNumberFromKey(stageKey) {
  const token = String(stageKey || '').toLowerCase();
  const match = token.match(/stage\s*(\d+(?:\.\d+)?)/i) || token.match(/(\d+(?:\.\d+)?)/);
  return match ? parseFloat(match[1]) : Number.NaN;
}

function pickBestStageEntry(stages, predicate) {
  if (!stages || typeof stages !== 'object') return null;
  let bestKey = null;
  let bestNumber = Number.NEGATIVE_INFINITY;
  Object.entries(stages).forEach(([key, value]) => {
    if (!predicate(key, value)) return;
    const stageNumber = getStageNumberFromKey(key);
    const score = Number.isNaN(stageNumber) ? 0 : stageNumber;
    if (score >= bestNumber) {
      bestNumber = score;
      bestKey = key;
    }
  });
  return bestKey != null ? stages[bestKey] : null;
}

function findJourneyReduxSnapshot(parsed) {
  if (!parsed || typeof parsed !== 'object') return null;
  const gateway = findPaymentGatewayStagePayload(parsed);
  if (
    gateway?.journeyReduxSnapshot &&
    typeof gateway.journeyReduxSnapshot === 'object'
  ) {
    return gateway.journeyReduxSnapshot;
  }
  let best = null;
  let bestNum = Number.NEGATIVE_INFINITY;
  for (const [key, value] of Object.entries(parsed)) {
    const snap = value?.journeyReduxSnapshot;
    if (!snap || typeof snap !== 'object' || Array.isArray(snap)) continue;
    const stageNumber = getStageNumberFromKey(key);
    const score = Number.isNaN(stageNumber) ? 0 : stageNumber;
    if (score >= bestNum) {
      bestNum = score;
      best = snap;
    }
  }
  return best;
}

function mergeQuoteResultForResume(base, overlay) {
  if (!overlay || typeof overlay !== 'object') return base;
  const merged = {
    ...(base && typeof base === 'object' ? base : {}),
    ...overlay,
  };
  if (overlay.biResult && typeof overlay.biResult === 'object') {
    merged.biResult = {
      ...(merged.biResult && typeof merged.biResult === 'object' ? merged.biResult : {}),
      ...overlay.biResult,
    };
  }
  const pdfFromOverlay =
    overlay.pdfUrl ||
    overlay.pdfURL ||
    overlay.biResult?.PDFURL ||
    overlay.biResult?.pdfUrl ||
    overlay.biResult?.pdfURL;
  if (pdfFromOverlay && !merged.pdfUrl && !merged.pdfURL) {
    merged.pdfUrl = pdfFromOverlay;
  }
  return merged;
}

/**
 * Restore slim Redux snapshot saved on summary/payment (PDF URL, riders, declarations).
 */
function hydrateFromJourneyReduxSnapshot(dispatch, parsed, attachAppNumber, getState) {
  const snapshot = findJourneyReduxSnapshot(parsed);
  if (!snapshot) return;

  const existingJourney =
    typeof getState === 'function' ? getState()?.journey : null;

  if (snapshot.quoteResult && typeof snapshot.quoteResult === 'object') {
    const merged = mergeQuoteResultForResume(
      existingJourney?.quoteResult,
      snapshot.quoteResult,
    );
    dispatch(journeyActions.setQuoteResult(attachAppNumber(merged)));
  }
  if (snapshot.premiumResult && typeof snapshot.premiumResult === 'object') {
    dispatch(journeyActions.setPremiumResult(snapshot.premiumResult));
  }
  if (snapshot.quoteRiders && typeof snapshot.quoteRiders === 'object') {
    dispatch(journeyActions.setQuoteRiders(snapshot.quoteRiders));
  }
  if (snapshot.quoteOptions && typeof snapshot.quoteOptions === 'object') {
    dispatch(journeyActions.setQuoteOptions(snapshot.quoteOptions));
  }
  if (snapshot.declarations && typeof snapshot.declarations === 'object') {
    dispatch(journeyActions.setDeclarations(snapshot.declarations));
  }
  if (snapshot.leadFormData && typeof snapshot.leadFormData === 'object') {
    dispatch(journeyActions.setLeadFormData(snapshot.leadFormData));
  }

  const gateway = findPaymentGatewayStagePayload(parsed);
  const quoteSummaryData = gateway?.quoteSummaryData;
  if (quoteSummaryData && typeof quoteSummaryData === 'object') {
    const currentQuote =
      typeof getState === 'function' ? getState()?.journey?.quoteResult : null;
    const withSummary = {
      ...(currentQuote && typeof currentQuote === 'object' ? currentQuote : {}),
      quoteSummaryData,
      totalPayableAmount:
        currentQuote?.totalPayableAmount ?? quoteSummaryData.totalPayableAmount,
    };
    dispatch(journeyActions.setQuoteResult(attachAppNumber(withSummary)));
  }
}

function mapStagesToResults(stages) {
  if (!stages || typeof stages !== 'object') {
    return { leadResult: null, quoteResult: null, premiumResult: null };
  }
  const leadResult = pickBestStageEntry(stages, (k) => String(k || '').toLowerCase().includes('lead'));
  const quoteResult =
    // Prefer Stage 3 quote payload (contains full quote fields used by quote page hydration).
    pickBestStageEntry(stages, (k, v) => {
      const lower = String(k || '').toLowerCase();
      return (
        lower.includes('3.0') &&
        lower.includes('quote') &&
        v &&
        typeof v === 'object' &&
        (v.coverAmount != null || v.leadInput != null || v.quoteOptions != null)
      );
    }) ||
    pickBestStageEntry(stages, (k, v) => {
      const lower = String(k || '').toLowerCase();
      return (
        lower.includes('quote') &&
        !lower.includes('summary') &&
        v &&
        typeof v === 'object' &&
        (v.coverAmount != null || v.leadInput != null || v.quoteOptions != null)
      );
    });
  const premiumResult =
    (quoteResult && typeof quoteResult === 'object' ? quoteResult.premiumResult : null) ||
    pickBestStageEntry(stages, (k) => String(k || '').toLowerCase().includes('premium'));
  return {
    leadResult: leadResult ?? null,
    quoteResult: quoteResult ?? null,
    premiumResult: premiumResult ?? null,
  };
}

function normalizeBooleanLike(value) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    if (v === 'true') return true;
    if (v === 'false') return false;
  }
  return Boolean(value);
}

function normalizePaymentTransactionStatus(value) {
  if (value == null) return null;
  let normalized = String(value).trim().toUpperCase();
  if (['FAILURE', 'REJECTED', 'DECLINED', 'CANCELLED', 'CANCELED'].includes(normalized)) {
    normalized = 'FAILED';
  }
  if (['COMPLETED', 'CAPTURED', 'PAID', 'SUCCESSFUL', 'SUCCEEDED'].includes(normalized)) {
    normalized = 'SUCCESS';
  }
  return ['INITIATED', 'PENDING', 'SUCCESS', 'FAILED'].includes(normalized)
    ? normalized
    : null;
}

/** Compact stage strings so "Payment Gateway Redirection" matches paymentgatewayredirection. */
function compactStageLower(stage) {
  return String(stage || '')
    .toLowerCase()
    .replace(/\s+/g, '');
}

/**
 * True when backend labels this as pay-first gateway return (Stage 5.0).
 * Handles spaced vs unspaced labels (e.g. "Payment Gateway Redirection").
 */
export function isPaymentGatewayRedirectionStage(stage) {
  const c = compactStageLower(stage);
  return c.includes('paymentgatewayredirection');
}

function txnStatusFromPaymentStageObject(obj) {
  if (!obj || typeof obj !== 'object') return null;
  const nested =
    obj.journeyReduxSnapshot && typeof obj.journeyReduxSnapshot === 'object'
      ? normalizePaymentTransactionStatus(obj.journeyReduxSnapshot.paymentTransactionStatus) ||
        normalizePaymentTransactionStatus(
          obj.journeyReduxSnapshot.journey?.paymentTransactionStatus,
        )
      : null;
  return (
    nested ||
    normalizePaymentTransactionStatus(obj.transactionStatus) ||
    normalizePaymentTransactionStatus(obj.paymentData?.transactionStatus) ||
    null
  );
}

/**
 * Locate Stage 5.0 payment gateway payload across cumulative + current stage keys.
 */
function findPaymentGatewayStagePayload(parsed) {
  if (!parsed || typeof parsed !== 'object') return null;
  const matches = [];
  const exact = parsed['Stage 5.0 PaymentGatewayRedirection'];
  if (exact && typeof exact === 'object' && !Array.isArray(exact)) {
    matches.push(exact);
  }
  const short = parsed.PaymentGatewayRedirection;
  if (short && typeof short === 'object' && !Array.isArray(short)) {
    matches.push(short);
  }
  for (const [key, value] of Object.entries(parsed)) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) continue;
    const k = compactStageLower(key);
    const inner = value.currentStage;
    const matchesKey =
      k.includes('paymentgatewayredirection') ||
      (k.includes('paymentgateway') && k.includes('redirection'));
    const matchesInner = inner != null && isPaymentGatewayRedirectionStage(inner);
    if (!matchesKey && !matchesInner) continue;
    matches.push(value);
  }
  const withSuccess = matches.find((value) => txnStatusFromPaymentStageObject(value) === 'SUCCESS');
  if (withSuccess) return withSuccess;
  const withFailed = matches.find((value) => txnStatusFromPaymentStageObject(value) === 'FAILED');
  if (withFailed) return withFailed;
  return matches[0] || null;
}

export function getPaymentTransactionStatusFromJourney(journey) {
  if (!journey || typeof journey !== 'object') return null;

  const parsed = normalizeJourneyStages(journey);
  const gatewayStage = findPaymentGatewayStagePayload(parsed);

  const fallbackPaymentStage = pickBestStageEntry(parsed, (_key, value) => {
    if (!value || typeof value !== 'object') return false;
    return (
      value.transactionStatus != null ||
      value.paymentData?.transactionStatus != null
    );
  });

  return (
    txnStatusFromPaymentStageObject(gatewayStage) ||
    normalizePaymentTransactionStatus(fallbackPaymentStage?.transactionStatus) ||
    normalizePaymentTransactionStatus(fallbackPaymentStage?.paymentData?.transactionStatus) ||
    normalizePaymentTransactionStatus(journey?.transactionStatus) ||
    null
  );
}

/**
 * Hydrate the journey Redux store from API journey data (same shape as usePaymentReturnResume).
 * @param {Function} dispatch - Redux dispatch
 * @param {Object} journey - Response from getJourneyData
 * @param {string} applicationNumber - Application number
 * @param {Function} [getState] - Redux getState (optional)
 * @param {{ forceQuoteScreen?: boolean }} [options] - When true, hydrate as if resuming on /quote (e.g. summary → back)
 */
export function hydrateStoreFromJourney(dispatch, journey, applicationNumber, getState, options = {}) {
  if (!journey || typeof journey !== 'object') return;
  const resumeRouteForHydration = options?.forceQuoteScreen
    ? '/quote'
    : getResumeRouteFromJourney(journey);
  const { plain: appNum } = resolveJourneyApplicationNumbers(
    journey.applicationNumber ||
      journey.application_number ||
      applicationNumber,
  );
  dispatch(journeyActions.setJourneyData(journey));
  const caseId = journey.caseId || journey.c_caseId || journey.case_number || null;
  if (appNum) dispatch(journeyActions.setApplicationNumber(appNum));
  dispatch(journeyActions.setCaseId(caseId));
  if (appNum) setStoredApplicationNumber(appNum);
  if (caseId) setStoredCaseId(caseId);

  dispatch(
    journeyActions.setPaymentTransactionStatus(
      getPaymentTransactionStatusFromJourney(journey),
    ),
  );

  const parsed = normalizeJourneyStages(journey);
  const { leadResult, quoteResult, premiumResult } = mapStagesToResults(parsed);

  const attachAppNumber = (snapshot) =>
    snapshot && appNum && typeof snapshot === 'object'
      ? { ...snapshot, applicationNumber: appNum }
      : snapshot;

  if (leadResult != null) {
    const leadWithApp = attachAppNumber(leadResult);
    dispatch(journeyActions.setLeadResult(leadWithApp));
    const merged = { ...leadWithApp };
    if (parsed && typeof parsed === 'object') {
      const leadStagesOnly = Object.fromEntries(
        Object.entries(parsed).filter(([key]) => String(key || '').toLowerCase().includes('lead')),
      );
      Object.assign(merged, mergeCumulativeStagesOrdered(leadStagesOnly));
    }
    merged.dateOfBirth = merged.dateOfBirth || merged.lifeAssuredDob || null;
    if (merged.dateOfBirth || merged.lifeAssuredDob || Object.keys(merged).length > 1) {
      dispatch(journeyActions.setLeadFormData(merged));
    }
  }
  if (quoteResult != null) dispatch(journeyActions.setQuoteResult(attachAppNumber(quoteResult)));
  if (premiumResult != null) dispatch(journeyActions.setPremiumResult(premiumResult));

  try {
    hydrateFromJourneyReduxSnapshot(dispatch, parsed, attachAppNumber, getState);
  } catch {
    /* best-effort */
  }

  /**
   * Quote page depends on `quoteInitResult` (effectiveLeadResult = quoteInitResult || leadResult)
   * to render options and trigger premium grid prefetch.
   *
   * IMPORTANT:
   * On some resumes (notably new-tab navigation), `mapStagesToResults()` may return `quoteResult === null`
   * while the backend still provides a dedicated `journey.quoteInitResult`.
   * So we must hydrate quoteInitResult independently of quoteResult extraction.
   */
  const quoteInitCandidate =
    journey?.quoteInitResult ||
    journey?.quoteInitResponse ||
    journey?.quote_init_result ||
    journey?.quote_init_response ||
    quoteResult;

  const looksLikeQuoteInit =
    quoteInitCandidate &&
    typeof quoteInitCandidate === 'object' &&
    (quoteInitCandidate.leadInput != null ||
      quoteInitCandidate.assignedProductName != null ||
      quoteInitCandidate.coverTillAgeOptions != null ||
      quoteInitCandidate.policyTermOptions != null ||
      quoteInitCandidate.premiumDefaults != null);

  // Only do this when we are actually resuming into Quote screen.
  if (resumeRouteForHydration === '/quote' && looksLikeQuoteInit) {
    // The persisted backend journey snapshot typically captures only the lead
    // input / assigned product and does NOT carry the dropdown option arrays
    // that the live `initQuote` API returns (e.g. `policyTermOptions`,
    // `coverTillAgeOptions`). If Quote.jsx has already fetched those options
    // (or a previous hydrate produced them) we must preserve them on a later
    // hydrate, otherwise `Quote.jsx`'s `shouldFetchQuoteInit` flips back to
    // true, causing a redundant `initQuote` call. If that retry races/fails
    // (e.g. 500/duplicate-session), the loader stays on indefinitely.
    const candidateWithApp = attachAppNumber(quoteInitCandidate);
    const existingQuoteInit =
      typeof getState === 'function' ? getState()?.journey?.quoteInitResult : null;
    let mergedQuoteInit = candidateWithApp;
    if (
      existingQuoteInit &&
      typeof existingQuoteInit === 'object' &&
      candidateWithApp &&
      typeof candidateWithApp === 'object'
    ) {
      const PRESERVE_KEYS = [
        'policyTermOptions',
        'coverTillAgeOptions',
        'premiumDefaults',
        'maxCoverAmount',
        'coverAmountOptions',
      ];
      const additions = {};
      for (const key of PRESERVE_KEYS) {
        if (candidateWithApp[key] == null && existingQuoteInit[key] != null) {
          additions[key] = existingQuoteInit[key];
        }
      }
      if (Object.keys(additions).length > 0) {
        mergedQuoteInit = { ...candidateWithApp, ...additions };
      }
    }
    dispatch(journeyActions.setQuoteInitResult(mergedQuoteInit));

    // Quote.jsx uses `leadFormData` for DOB/name display (and some modals),
    // so populate the minimal fields from quoteInitResult.
    const dob =
      quoteInitCandidate.dateOfBirth ||
      quoteInitCandidate.lifeAssuredDob ||
      quoteInitCandidate.leadInput?.lifeAssuredDob ||
      quoteInitCandidate.leadInput?.dateOfBirth ||
      null;
    const fullName =
      quoteInitCandidate.fullName ||
      quoteInitCandidate.lifeAssuredName ||
      quoteInitCandidate.name ||
      quoteInitCandidate.leadInput?.lifeAssuredName ||
      quoteInitCandidate.leadInput?.fullName ||
      quoteInitCandidate.leadInput?.name ||
      '';

    if (dob || fullName) {
      dispatch(
        journeyActions.setLeadFormData({
          ...(quoteInitCandidate.leadInput && typeof quoteInitCandidate.leadInput === 'object'
            ? quoteInitCandidate.leadInput
            : quoteInitCandidate),
          dateOfBirth: dob || undefined,
          fullName: fullName || undefined,
        }),
      );
    }
  }

  /**
   * Hydrate proposal stepData so proposal/verification screens can prefill on:
   * - hard refresh
   * - opening the same journey URL in a new tab
   *
   * UI reads from:
   * - state.journey.stepData.lifeAssured
   * - state.journey.stepData.nominee
   * - state.journey.stepData.medical
   */
  try {
    if (parsed && typeof parsed === 'object') {
      const lifeStages = {};
      const nomineeStages = {};
      const medicalStages = {};
      const documentsFlat = {};

      for (const [k, v] of Object.entries(parsed)) {
        if (!v || typeof v !== 'object' || Array.isArray(v)) continue;
        const tab = v.tab;
        if (tab === 'lifeAssured') lifeStages[k] = v;
        if (tab === 'nominee') nomineeStages[k] = v;
        if (tab === 'medical') medicalStages[k] = v;

        const docs = v.proposalDocumentsSnapshot;
        if (docs && typeof docs === 'object' && !Array.isArray(docs)) {
          Object.assign(documentsFlat, docs);
        }
      }

      Object.assign(documentsFlat, mergeDocumentsUploadedFromParsedStages(parsed));
      Object.assign(documentsFlat, collectDocumentsUploadedDeep(journey));

      const mergedLifeAssured =
        Object.keys(lifeStages).length > 0 ? mergeCumulativeStagesOrdered(lifeStages) : null;
      const mergedNominee =
        Object.keys(nomineeStages).length > 0 ? mergeCumulativeStagesOrdered(nomineeStages) : null;
      const mergedMedical =
        Object.keys(medicalStages).length > 0 ? mergeCumulativeStagesOrdered(medicalStages) : null;

      if (mergedLifeAssured && Object.keys(mergedLifeAssured).length > 0) {
        dispatch(journeyActions.saveStepData({ step: 'lifeAssured', data: mergedLifeAssured }));
      }
      if (mergedNominee && Object.keys(mergedNominee).length > 0) {
        dispatch(journeyActions.saveStepData({ step: 'nominee', data: mergedNominee }));
      }
      if (mergedMedical && Object.keys(mergedMedical).length > 0) {
        dispatch(journeyActions.saveStepData({ step: 'medical', data: mergedMedical }));
      }

      if (Object.keys(documentsFlat).length > 0) {
        const existing =
          typeof getState === 'function' ? getState()?.journey?.documents : undefined;
        const base =
          existing && typeof existing === 'object' && !Array.isArray(existing)
            ? existing
            : {};
        const normalizedFlat = {};
        for (const [slotKey, slotVal] of Object.entries(documentsFlat)) {
          if (!slotVal || typeof slotVal !== 'object' || Array.isArray(slotVal)) continue;
          const fromParser = parseDocumentsUploadedToReduxSlots({ [slotKey]: slotVal })[slotKey];
          if (fromParser) {
            normalizedFlat[slotKey] = fromParser;
            continue;
          }
          if (slotVal.__fileMeta === true) {
            normalizedFlat[slotKey] = {
              ...slotVal,
              ...pickDocumentPreviewFields(slotVal),
              name: String(slotVal.name || slotVal.fileName || 'document'),
              __fileMeta: true,
            };
          } else {
            normalizedFlat[slotKey] = slotVal;
          }
        }
        dispatch(journeyActions.setDocuments({ ...base, ...normalizedFlat }));
      }
    }
  } catch {
    /* best-effort hydration only */
  }

  // Hydrate Customer Verification snapshot (PAN/Aadhaar verification state) for resume in new tabs.
  try {
    if (parsed && typeof parsed === 'object') {
      const customerVerificationStage = pickBestStageEntry(parsed, (k, v) => {
        const lower = String(k || '').toLowerCase();
        if (lower.includes('customerverification')) return true;
        if (lower.includes('stage 7') || lower.includes('7.0')) return true;
        return (
          v &&
          typeof v === 'object' &&
          (v.customerVerificationSnapshot != null ||
            v.prefillData != null ||
            v.panVerified != null ||
            v.aadharVerified != null)
        );
      });

      const cvSource =
        customerVerificationStage?.customerVerificationSnapshot &&
        typeof customerVerificationStage.customerVerificationSnapshot === 'object'
          ? customerVerificationStage.customerVerificationSnapshot
          : customerVerificationStage;

      if (cvSource && typeof cvSource === 'object') {
        const panNumber = cvSource?.pan?.number || cvSource?.prefillData?.panNumber || '';
        const panName = cvSource?.prefillData?.panName || '';
        const panVerified = normalizeBooleanLike(
          cvSource?.pan?.verified ?? cvSource?.panVerified ?? false,
        );

        const aadhaarNumber =
          cvSource?.aadhaar?.number || cvSource?.prefillData?.aadhaarNumber || '';
        const aadhaarVerified = normalizeBooleanLike(
          cvSource?.aadhaar?.verified ?? cvSource?.aadharVerified ?? false,
        );

        const payload = {
          ...(cvSource && typeof cvSource === 'object' ? cvSource : {}),
          prefillData: {
            ...(cvSource?.prefillData && typeof cvSource.prefillData === 'object'
              ? cvSource.prefillData
              : {}),
            panNumber: panNumber || undefined,
            panName: panName || undefined,
            aadhaarNumber: aadhaarNumber || undefined,
          },
          pan: {
            ...(cvSource?.pan && typeof cvSource.pan === 'object' ? cvSource.pan : {}),
            number: panNumber || undefined,
            verified: panVerified,
          },
          aadhaar: {
            ...(cvSource?.aadhaar && typeof cvSource.aadhaar === 'object' ? cvSource.aadhaar : {}),
            number: aadhaarNumber || undefined,
            verified: aadhaarVerified,
          },
          panVerified: panVerified ? 'true' : 'false',
          aadharVerified: aadhaarVerified ? 'true' : 'false',
        };

        dispatch(journeyActions.setCustomerVerificationData(payload));
      }
    }
  } catch {
    /* best-effort */
  }

  /**
   * Hydrate Quote "Life Cover" selection into Redux.
   * Quote.jsx restores these values from Redux, so new tabs can render the exact same cover.
   */
  try {
    const existingJourney =
      typeof getState === 'function' ? getState()?.journey : null;
    const existingPlain = resolveJourneyApplicationNumbers(
      existingJourney?.applicationNumber,
    ).plain;
    const sameAppNumber =
      existingPlain != null &&
      appNum != null &&
      String(existingPlain) === String(appNum);

    const hasPersistedCoverSelection =
      existingJourney?.quoteCoverAmount != null ||
      (existingJourney?.quoteCustomCoverAmount != null &&
        String(existingJourney.quoteCustomCoverAmount) !== '') ||
      existingJourney?.quoteCoverTillAge != null ||
      (existingJourney?.quoteCustomCoverTillAge != null &&
        String(existingJourney.quoteCustomCoverTillAge) !== '');

    // If backend has saved cover/till-age for this journey, it is the source of truth on resume.
    // Only keep local persisted cover selections when backend doesn't provide any values.
    const hasBackendCoverSelection =
      quoteResult?.coverAmount != null ||
      (quoteResult?.customCoverAmount != null &&
        String(quoteResult.customCoverAmount) !== '') ||
      quoteResult?.coverTillAge != null ||
      (quoteResult?.customCoverTillAge != null &&
        String(quoteResult.customCoverTillAge) !== '');
    const shouldHydrateCoverSelection =
      hasBackendCoverSelection || !(sameAppNumber && hasPersistedCoverSelection);

    const quoteStageLike = quoteResult && typeof quoteResult === 'object' ? quoteResult : null;

    let coverAmountCandidate = quoteStageLike?.coverAmount ?? null;
    let customCoverAmountCandidate =
      quoteStageLike?.customCoverAmount != null ? quoteStageLike.customCoverAmount : '';
    customCoverAmountCandidate =
      customCoverAmountCandidate != null ? String(customCoverAmountCandidate) : '';

    let coverTillAgeCandidate = quoteStageLike?.coverTillAge ?? null;
    let customCoverTillAgeCandidate =
      quoteStageLike?.customCoverTillAge != null ? quoteStageLike.customCoverTillAge : '';
    customCoverTillAgeCandidate =
      customCoverTillAgeCandidate != null ? String(customCoverTillAgeCandidate) : '';

    // Fallback: if backend sometimes stores cover data in QuoteSummary stage
    if (
      (coverAmountCandidate == null || coverTillAgeCandidate == null) &&
      parsed &&
      typeof parsed === 'object'
    ) {
      const summaryKey = Object.keys(parsed).find((k) => {
        const lower = String(k || '').toLowerCase();
        return lower.includes('quotesummary') || (lower.includes('quote') && lower.includes('summary'));
      });
      const summaryStage = summaryKey ? parsed[summaryKey] : null;
      const summaryData = summaryStage?.summaryData || summaryStage?.quoteSummaryData || null;

      if (summaryData && typeof summaryData === 'object') {
        if (coverAmountCandidate == null && summaryData.coverAmount != null) {
          coverAmountCandidate = summaryData.coverAmount;
        }
        if (coverTillAgeCandidate == null && summaryData.coverTillAge != null) {
          coverTillAgeCandidate = summaryData.coverTillAge;
        }
        if (
          (customCoverAmountCandidate == null || customCoverAmountCandidate === '') &&
          summaryData.customCoverAmount != null
        ) {
          customCoverAmountCandidate = summaryData.customCoverAmount;
        }
        if (
          (customCoverTillAgeCandidate == null || customCoverTillAgeCandidate === '') &&
          summaryData.customCoverTillAge != null
        ) {
          customCoverTillAgeCandidate = summaryData.customCoverTillAge;
        }
      }
    }

    const selectedCoverAmount =
      customCoverAmountCandidate !== ''
        ? null
        : coverAmountCandidate != null && String(coverAmountCandidate).trim() !== ''
          ? Number(coverAmountCandidate)
          : null;
    const selectedCoverTillAge =
      customCoverTillAgeCandidate !== ''
        ? null
        : coverTillAgeCandidate != null && String(coverTillAgeCandidate).trim() !== ''
          ? Number(coverTillAgeCandidate)
          : null;

    // Only dispatch when at least one value exists.
    if (shouldHydrateCoverSelection) {
      if (
        selectedCoverAmount != null ||
        customCoverAmountCandidate !== '' ||
        selectedCoverTillAge != null ||
        customCoverTillAgeCandidate !== ''
      ) {
        dispatch(
          journeyActions.setQuoteCoverAmountSelection({
            selectedCoverAmount:
              selectedCoverAmount != null && !Number.isNaN(selectedCoverAmount)
                ? selectedCoverAmount
                : null,
            customCoverAmount: customCoverAmountCandidate ?? '',
          }),
        );
        dispatch(
          journeyActions.setQuoteCoverTillAgeSelection({
            selectedCoverTillAge:
              selectedCoverTillAge != null && !Number.isNaN(selectedCoverTillAge)
                ? selectedCoverTillAge
                : null,
            customCoverTillAge: customCoverTillAgeCandidate ?? '',
          }),
        );
      }
    }
  } catch {
    /* best-effort */
  }

  const proposalProgress = buildProposalProgressFromCumulative(parsed);
  if (proposalProgress != null) {
    dispatch(journeyActions.setProposalData(proposalProgress));
  }

  if (resumeRouteForHydration === '/summary') dispatch(journeyActions.setStage('summary'));
  else if (resumeRouteForHydration === '/verification') dispatch(journeyActions.setStage('customerVerification'));
  else if (resumeRouteForHydration === '/proposal') dispatch(journeyActions.setStage('proposal'));
  else if (resumeRouteForHydration === '/proposal-review') dispatch(journeyActions.setStage('proposal'));
  else if (resumeRouteForHydration === '/quote') dispatch(journeyActions.setStage('quote'));
  else dispatch(journeyActions.setStage('lead'));
}

/**
 * Get current stage value from journey (backend may use currentStage or c_currentStage).
 */
function getCurrentStageValue(journey) {
  const parsed = normalizeJourneyStages(journey);
  const currentStageData = parseStages(journey.currentStageData ?? journey.c_currentStageData);
  const currentStageDataKey =
    currentStageData && typeof currentStageData === 'object'
      ? Object.keys(currentStageData)[0]
      : '';
  const declaredRaw =
    journey.currentStage ??
    journey.current_stage ??
    journey.c_currentStage ??
    journey.currentStageData?.currentStage ??
    currentStageDataKey ??
    null;
  const declared =
    declaredRaw != null && String(declaredRaw).trim() !== ''
      ? String(declaredRaw).trim()
      : '';
  const furthest = getFurthestStageToken(parsed, declared);

  if (furthest) {
    const furthestRank = rankJourneyStage(furthest);
    const declaredRank = rankJourneyStage(declared);
    if (!declared || furthestRank >= declaredRank) {
      return furthest;
    }
  }
  if (declared) return declared;
  return getMaxStageTokenFromJourney(journey, parsed);
}

/**
 * Infer from stages object keys whether lead is complete (e.g. "Lead Stage 2" or key containing "2").
 */
function isLeadCompleteFromStages(parsed) {
  if (!parsed || typeof parsed !== 'object') return false;
  const keys = Object.keys(parsed);
  const hasLeadStageAtOrBeyond2 = keys.some((k) => {
    const lower = String(k || '').toLowerCase();
    if (!lower.includes('lead')) return false;
    const m = String(k).match(/(\d+(?:\.\d+)?)/);
    if (!m) return false;
    const n = parseFloat(m[1]);
    return !Number.isNaN(n) && n >= 2;
  });
  if (hasLeadStageAtOrBeyond2) return true;

  return keys.some((k) => k.toLowerCase().includes('quote') || k.toLowerCase().includes('premium'));
}

/**
 * Infer the internal route to resume at from journey data.
 * Returns null if the journey is still on lead (stay on /term-plan Lead personalize).
 * @param {Object} journey - Response from getJourneyData
 * @returns {string|null} Route path e.g. '/quote', '/summary', '/verification', '/proposal', '/proposal-review', or null
 */
export function getResumeRouteFromJourney(journey) {
  if (!journey || typeof journey !== 'object') return null;

  const currentStage = getCurrentStageValue(journey);
  const currentStageLower = String(currentStage || '').toLowerCase();
  const parsed = normalizeJourneyStages(journey);
  const currentStageNumberMatch = currentStage.match(/(\d+(?:\.\d+)?)/);
  const currentStageNumber = currentStageNumberMatch ? parseFloat(currentStageNumberMatch[1]) : Number.NaN;
  const parsedEntries = parsed && typeof parsed === 'object' ? Object.entries(parsed) : [];
  const stage85Entry = parsedEntries.find(([key]) =>
    String(key || '').toLowerCase().includes('8.5'),
  );
  const stage85Data =
    stage85Entry && stage85Entry[1] && typeof stage85Entry[1] === 'object'
      ? stage85Entry[1]
      : null;
  const isStage85Completed = Boolean(
    stage85Data &&
      (String(stage85Data.declarationConsentsAccepted || '').toLowerCase() === 'true' ||
        (Array.isArray(stage85Data.declarationConsents) &&
          stage85Data.declarationConsents.length > 0 &&
          stage85Data.declarationConsents.every(Boolean)) ||
        (stage85Data.declarationConsents &&
          typeof stage85Data.declarationConsents === 'object' &&
          Object.keys(stage85Data.declarationConsents).length > 0 &&
          Object.values(stage85Data.declarationConsents).every((value) =>
            String(value).toLowerCase() === 'true' || value === true,
          ))),
  );

  // Same `currentStage` string for pay-first gateway; route by `transactionStatus` on stage payload.
  // Use compact match so "Payment Gateway Redirection" (spaces) is not mistaken for generic stage 5 → /quote.
  if (isPaymentGatewayRedirectionStage(currentStage)) {
    const paymentTxnStatus = getPaymentTransactionStatusFromJourney(journey);
    if (paymentTxnStatus === 'SUCCESS') return '/verification';
    if (paymentTxnStatus === 'FAILED') return '/summary';
    // INITIATED / PENDING / unknown: not completed — summary to pay or retry
    return '/summary';
  }

  // Explicit direct stage labels from backend/session snapshots.
  if (
    currentStageLower === 'summary' ||
    currentStageLower.includes('quote summary') ||
    currentStageLower.includes('quotesummary')
  ) {
    return '/summary';
  }
  if (
    currentStageLower === 'verification' ||
    currentStageLower === 'customerverification' ||
    currentStageLower === 'customer-verification' ||
    currentStageLower.includes('customer verification')
  ) {
    return '/verification';
  }
  if (
    currentStageLower === 'proposal-review' ||
    currentStageLower.includes('proposalreview') ||
    currentStageLower.includes('thankyou')
  ) {
    return '/proposal-review';
  }

  if (
    currentStageLower.includes('8.5') ||
    currentStageLower === 'pr_declaration' ||
    currentStageLower === 'pl_declaration' ||
    isStage85Completed
  ) {
    return '/proposal-review';
  }

  // Map backend stage names to internal route (priority order: later stages first)
  if (currentStage.includes('10.') || currentStage.includes('ThankYou')) {
    return '/proposal-review';
  }
  if (currentStage.includes('9.') || currentStage.includes('ProposalSummary') || currentStage.includes('OTP')) {
    return '/proposal-review';
  }
  if (
    currentStageLower === 'proposal' ||
    currentStageLower.includes('proposalform') ||
    currentStage.includes('8.')
  ) {
    return '/proposal';
  }
  if (
    currentStage.includes('7.') ||
    currentStage.includes('5.0 CustomerVerification') ||
    (currentStage.includes('CustomerVerification') &&
      !isPaymentGatewayRedirectionStage(currentStage))
  ) {
    return '/verification';
  }
  if (currentStage.includes('4.0') || currentStage.includes('QuoteSummary')) {
    return '/summary';
  }

  // Explicitly keep lead stages (1.0/1.1/1.2) on lead screen.
  if (currentStage.toLowerCase().includes('lead') && !Number.isNaN(currentStageNumber) && currentStageNumber <= 2) {
    return null;
  }

  // Lead complete (stage 2 or Stage 2.0 / 3.0 Quote): go to quote so we don't show lead again after remount/refresh
  if (
    (!Number.isNaN(currentStageNumber) && currentStageNumber >= 3) ||
    currentStage.includes('3.0') ||
    currentStage.includes('Stage 3') ||
    currentStage.toLowerCase().includes('quote')
  ) {
    return '/quote';
  }

  // Infer from stages object keys (API may return currentStage as c_currentStage or omit it; cumulative may have "Lead Stage 2")
  if (isLeadCompleteFromStages(parsed)) {
    return '/quote';
  }

  // Fallback: infer from presence of stage data (quote/premium = at least quote; summary/verification/proposal keys)
  if (parsed && typeof parsed === 'object') {
    const keys = Object.keys(parsed).map((k) => k.toLowerCase());
    if (keys.some((k) => k.includes('proposal') || k.includes('8.') || /^(pr_|pl_|la_|ml_)/.test(k))) {
      return keys.some((k) => k.includes('8.5') || k === 'pr_declaration' || k === 'pl_declaration')
        ? '/proposal-review'
        : '/proposal';
    }
    if (keys.some((k) => k.includes('verification') || k.includes('customer'))) return '/verification';
    if (keys.some((k) => k.includes('summary') || k.includes('quote summary') || k.includes('4.'))) return '/summary';
    if (keys.some((k) => k.includes('quote') || k.includes('premium'))) return '/quote';
  }

  return null;
}

/**
 * Build route state for proposal resume so UI opens the exact tab/section.
 * Used with navigate('/proposal', { state }).
 * @param {Object} journey - Response from getJourneyData
 * @returns {Object|null}
 */
export function getResumeStateFromJourney(journey) {
  if (!journey || typeof journey !== 'object') return null;
  const currentStage = String(getCurrentStageValue(journey) || '').toLowerCase();
  if (!currentStage.includes('8.')) return null;

  if (currentStage.includes('8.0') || currentStage.includes('screen1')) {
    return { tab: 'lifeAssured', section: 'personal' };
  }
  if (currentStage.includes('8.1') || currentStage.includes('screen2')) {
    return { tab: 'lifeAssured', section: 'bank' };
  }
  if (currentStage.includes('8.2') || currentStage.includes('screen3')) {
    return { tab: 'lifeAssured', section: 'additional' };
  }
  if (currentStage.includes('8.3') || currentStage.includes('screen4')) {
    return { tab: 'nominee' };
  }
  if (currentStage.includes('8.4') || currentStage.includes('screen5') || currentStage.includes('8.5') || currentStage.includes('screen6')) {
    return { tab: 'medical' };
  }
  return { tab: 'lifeAssured', section: 'personal' };
}

/**
 * Map lead currentStage (1.0, 1.1, 1.2, 2) to Lead form step index (0-based).
 * Used so we don't show step 1 again when user was on step 2.
 * @param {number|string} currentStage - e.g. 1.0, 1.1, 1.2, 2
 * @returns {number} Step index 0--3, or 1 as default for personalize
 */
export function leadStageToStepIndex(currentStage) {
  const n = parseStageNumber(currentStage);
  if (Number.isNaN(n)) return 1;
  // Business rule: when backend says Stage 1.0 Lead, open next UI step (1.1) directly.
  if (n === 1.0) return 1;
  if (n <= 1.1) return 1;
  if (n <= 1.2) return 2;
  return 3;
}
